// The contract between the read-time statement extractor and the mobile UI.
//
// IMPORTANT: this JSON is NOT emitted by the AI script at generation time. The
// spreadsheet stays the calculation engine — the script lays down stable
// *anchors* (which cells/regions belong to which technician, which are canonical
// named values), and a read-time extractor follows those anchors to the CURRENT
// cell values and projects them into this shape. Editing the sheet → recalc →
// the next extraction reflects it. The UI never reads cell positions; it just
// renders these blocks in order, so a business/plan can compose any statement
// freely (commission-only, hourly-only, hourly + commission, …).
//
// All values are pre-formatted display strings — the extractor formats numbers
// using each anchored cell's value_type, so the UI does no number formatting.

export const BLOCK_TYPE = {
  summary: "summary",
  section: "section",
  keyValue: "keyValue",
  totals: "totals",
  note: "note",
  divider: "divider",
} as const;

export type BlockType = (typeof BLOCK_TYPE)[keyof typeof BLOCK_TYPE];

export interface Metric {
  /** May be absent/null: e.g. an unlabeled totals figure from the extractor. */
  label?: string | null;
  value: string;
  /** Render the value with emphasis (e.g. a money figure). */
  strong?: boolean;
}

/** A headline number + a grid of supporting metrics. */
export interface SummaryBlock {
  type: typeof BLOCK_TYPE.summary;
  hero?: Metric;
  metrics?: Metric[];
  /**
   * Visual treatment for pay-source cards where only one of several sources is
   * actually paid (e.g. the higher of commission vs. hourly guarantee). "paid"
   * highlights the card; "unpaid" de-emphasizes it (greyed). Omit for a plain
   * card (e.g. the overview hero) — it renders with no container.
   */
  variant?: "paid" | "unpaid";
}

export interface LineItem {
  /** Small line above the title, e.g. a date. */
  eyebrow?: string;
  title: string;
  /** The pay figure for this line, right-aligned. */
  amount?: string;
  /** Supporting key/value detail shown beneath the title. */
  fields?: Metric[];
  /** A free-text note (full width, de-emphasized). */
  note?: string;
}

/** A titled list of line items (jobs, hourly entries, spiffs, …). */
export interface SectionBlock {
  type: typeof BLOCK_TYPE.section;
  heading?: string;
  /**
   * A lighter secondary line beneath the heading — e.g. for a job group, the
   * date + "Invoice <n>". Less visually heavy than the heading (the name leads).
   */
  caption?: string;
  /**
   * Optional subtotal shown on the section header, right-aligned — used when a
   * section is one job/invoice group (its commission items summed).
   */
  total?: Metric;
  items: LineItem[];
}

/** A simple labelled-value list. */
export interface KeyValueBlock {
  type: typeof BLOCK_TYPE.keyValue;
  heading?: string;
  rows: Metric[];
}

/** An emphasized totals strip. */
export interface TotalsBlock {
  type: typeof BLOCK_TYPE.totals;
  label: string;
  items: Metric[];
}

export interface NoteBlock {
  type: typeof BLOCK_TYPE.note;
  text: string;
}

/**
 * A labelled separator that introduces the run of blocks beneath it — e.g. one
 * per source sheet when a technician's pay spans several sheets (SATX `… - CC` /
 * `… - SA`, or Dean's department sheets). It's just another block in the list, so
 * grouping needs no separate structure: the assembly emits a divider, then that
 * sheet's blocks, then the next divider, etc.
 */
export interface DividerBlock {
  type: typeof BLOCK_TYPE.divider;
  label: string;
}

export type StatementBlock =
  | SummaryBlock
  | SectionBlock
  | KeyValueBlock
  | TotalsBlock
  | NoteBlock
  | DividerBlock;

export interface PayStatement {
  /** Usually the technician's name. */
  title: string;
  /** e.g. the pay period and/or role. */
  subtitle?: string;
  /**
   * The whole statement, in render order. Grouping (e.g. per source sheet) is
   * expressed inline via `divider` blocks — there is no separate grouped shape,
   * so one renderer draws every layout: flat, stacked, or merged.
   */
  blocks: StatementBlock[];
}
